import { Form, Input, Modal, Flex, Switch, Tooltip } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';

import { FormFooter } from './components/FormFooter';
import { ScheduledDateDisplay } from './components/ScheduledDateDisplay';
import { SchedulingModal } from './components/SchedulingModal';
import { usePostFormLogic } from './hooks';
import { NoTagsSelectedAlert, TitleWrapper } from './PostForm.styles';
import { TopicSelector } from './TopicSelector';

import { HtmlEditor, TagSelector } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Upload } from '@/components/Upload';

type Props = {
  isEditable?: boolean;
};

const PREVIEW_SIZE = {
  width: '100%',
  height: 200
};

const MODAL_MAX_WIDTH = 760;

export const PostForm: React.FC<Props> = ({ isEditable }) => {
  const { t } = useTranslation('CommonDashboardTranslations');
  const {
    isOpen,
    post,
    showScheduleModal,
    isEditingScheduledPost,
    isPending,
    postForm,
    scheduleForm,
    closeModal,
    handlePostFormSubmit,
    saveAsDraft,
    publishNow,
    openScheduleModal,
    handleScheduleSubmit,
    setShowScheduleModal
  } = usePostFormLogic();

  const tags = useWatch('tags', postForm);
  const hasTagsSelected = !!tags?.length;
  const formScheduledDate = useWatch('scheduledPublishDate', postForm);

  const title = useWatch('title', postForm);
  const topic = useWatch('topic', postForm);
  const featuredImage = useWatch('featuredImage', postForm);
  const content = useWatch('content', postForm);

  const hasAnyRequiredField = !!(title || topic || featuredImage?.url || content);

  if (!isEditable) return null;

  return (
    <>
      <Modal
        forceRender
        open={isOpen}
        centered
        destroyOnHidden
        style={{ top: 0 }}
        styles={{
          content: {
            padding: '20px 24px 0'
          }
        }}
        onCancel={closeModal}
        width={MODAL_MAX_WIDTH}
        title={<TitleWrapper>{t(post ? 'EDIT_NEWS' : 'CREATE_NEWS')}</TitleWrapper>}
        footer={
          <FormFooter
            post={post}
            isPending={isPending}
            onSaveAsDraft={saveAsDraft}
            onSchedule={openScheduleModal}
            onPublishNow={publishNow}
            onSaveChanges={() => postForm.submit()}
            canSaveAsDraft={hasAnyRequiredField}
          />
        }>
        <Form form={postForm} onFinish={handlePostFormSubmit} layout="vertical">
          <Flex gap={24} vertical>
            <Form.Item name="_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="status" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="scheduledPublishDate" hidden />
            <Flex gap={16} flex="1 1 0">
              <Form.Item
                label={t('IMAGE')}
                name="featuredImage"
                style={{ flex: '1 1 0' }}
                rules={[
                  {
                    validator: (_, value) =>
                      value?.url
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('FIELD_REQUIRED')))
                  }
                ]}>
                <Upload
                  accept="image/*"
                  preview
                  width={PREVIEW_SIZE.width}
                  height={PREVIEW_SIZE.height}>
                  <Icon i="ImageReference" />
                </Upload>
              </Form.Item>
              <Flex vertical flex="1 1 0">
                <Form.Item
                  name="title"
                  label={t('POST_TITLE')}
                  rules={[{ required: true, message: t('FIELD_REQUIRED') }]}>
                  <Input placeholder={t('POST_TITLE_PLACEHOLDER')} />
                </Form.Item>
                <Form.Item
                  label={t('TOPIC')}
                  name="topic"
                  rules={[{ required: true, message: t('FIELD_REQUIRED') }]}>
                  <TopicSelector />
                </Form.Item>
                <Form.Item name="tags" label={t('TAGS')}>
                  <TagSelector mode="multiple" style={{ width: '100%' }} />
                </Form.Item>
              </Flex>
            </Flex>
            {!hasTagsSelected && (
              <NoTagsSelectedAlert>
                <Icon i="InformationFilled" color="purple6" /> {t('NO_TAGS_SELECTED_ALERT')}
              </NoTagsSelectedAlert>
            )}
            <Form.Item
              name="content"
              label={t('POST_CONTENT')}
              rules={[{ required: true, message: t('FIELD_REQUIRED') }]}>
              <HtmlEditor />
            </Form.Item>
            <Flex vertical gap={8}>
              <ScheduledDateDisplay
                post={post}
                formScheduledDate={formScheduledDate}
                onEdit={() => setShowScheduleModal(true)}
              />
            </Flex>
            <Flex gap={8} align="center">
              <Form.Item noStyle name="isFeatured" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Flex gap={4} align="center">
                <div>{t('FEATURE_POST')}</div>
                <Tooltip title={t('FEATURE_POST_TOOLTIP')} placement="top">
                  <span style={{ cursor: 'help' }}>
                    <Icon i="InformationFilled" />
                  </span>
                </Tooltip>
              </Flex>
            </Flex>
            <Flex gap={8} align="center">
              <Form.Item noStyle name="sendEmailNotification" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Flex gap={4} align="center">
                <div>{t('SEND_EMAIL_NOTIFICATION')}</div>
                <Tooltip title={t('SEND_EMAIL_NOTIFICATION_TOOLTIP')} placement="top">
                  <span style={{ cursor: 'help' }}>
                    <Icon i="InformationFilled" />
                  </span>
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>
        </Form>
      </Modal>

      <SchedulingModal
        isOpen={showScheduleModal}
        isEditingScheduledPost={isEditingScheduledPost}
        isPending={isPending}
        scheduleForm={scheduleForm}
        onSubmit={handleScheduleSubmit}
        onCancel={() => setShowScheduleModal(false)}
      />
    </>
  );
};
